// @flow

import {
  createRecordAsync,
  fetchOnceAsync,
  updateRecordAsync
} from '../services/firebaseDatabase'
import type { SubscriptionLengthType } from '../flowTypes'

export function validateCode (code: string): boolean {
  const generatedCode = /^[A-Z0-9_]{4}-[A-Z0-9_]{4}-[A-Z0-9_]{4}$/
  const printedCode = /^[A-Z0-9]{8}$/
  const shortCode = /^[A-Z0-9]{6}$/

  return generatedCode.test(code) || printedCode.test(code) || shortCode.test(code)
}

export type GiftCodeType = {
  code: string,
  created_at: number
}

export async function getPurchasedGiftCardCodeAsync (code: string | null): Promise<?GiftCodeType> {
  if (code) {
    return fetchOnceAsync(`/giftCardCodes/${code}`)
  }
}

export async function setRedeemedOnUser (userId: string, code: string): Promise<void> {
  return updateRecordAsync(`/users/${userId}`, {
    isSubscribed: true,
    redeemed: {
      code,
      timestamp: Date.now()
    }
  })
}

export type PurchaseGiftParamsType = {
  token?: string,
  purchaserName: string,
  purchaserEmail: string,
  sendToSelf: boolean,
  recipientEmail?: string,
  recipientName?: string,
  mailGift: boolean,
  address1?: string,
  address2?: string,
  city?: string,
  state?: string,
  zipCode?: string,
  cardTo?: string,
  cardFrom?: string,
  paymentSource?: string,
  quantity?: number,
  promo?: string,
  price?: number,
  discount?: string,
  subscriptionGiftType: SubscriptionLengthType
}

const errorMessages = {
  purchaserName: 'Missing purchaser name',
  purchaserEmail: 'Missing purchaser email',
  recipientName: 'Missing recipient name',
  recipientEmail: 'Missing recipient email',
  address1: 'Missing address1',
  city: 'Missing city',
  state: 'Missing state',
  zipCode: 'Missing zipcode',
  cardTo: 'Missing card to name',
  cardFrom: 'Missing card from name'
}

export async function setGiftPurchaseAsync (params: PurchaseGiftParamsType): Promise<string> {
  if (!params.token) {
    throw new Error('No token for gift purchase')
  }
  ['purchaserName', 'purchaserEmail'].forEach((item: string): void => {
    if (!params[item]) {
      throw new Error(errorMessages[item])
    }
  })

  if (params.mailGift && !params.sendToSelf) { // note: 'address2' is not required
    ['recipientName', 'address1', 'city', 'state', 'zipCode', 'cardTo', 'cardFrom'].forEach((item: string): void => {
      if (!params[item]) {
        throw new Error(errorMessages[item])
      }
    })
  }

  if (params.mailGift && params.sendToSelf) { // note: 'address2' is not required
    ['recipientName', 'address1', 'city', 'state', 'zipCode'].forEach((item: string): void => {
      if (!params[item]) {
        throw new Error(errorMessages[item])
      }
    })
  }

  if (!params.mailGift && !params.sendToSelf) {
    ['recipientName', 'recipientEmail'].forEach((item: string): void => {
      if (!params[item]) {
        throw new Error(errorMessages[item])
      }
    })
  }

  if (!params.paymentSource || !params.quantity) {
    params.paymentSource = params.token
    params.quantity = 1
  }

  const newRecordKey = await createRecordAsync('/giftCardPurchases/', params)
  return newRecordKey
}
